import React, { useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  FileText, 
  Upload, 
  Download, 
  CheckCircle, 
  AlertCircle, 
  Loader2,
  FileSpreadsheet,
  Building2,
  ArrowRight,
  CreditCard,
  Info,
  Lock
} from 'lucide-react';
import { useDropzone } from 'react-dropzone';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';
import { Link } from 'react-router-dom';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const MT940Page = () => {
  const { language } = useLanguage();
  const { user, token, isPro } = useAuth();
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [downloading, setDownloading] = useState(false);

  const onDrop = useCallback((acceptedFiles) => {
    const pdfFile = acceptedFiles.find(f => f.type === 'application/pdf' || f.name.toLowerCase().endsWith('.pdf'));
    if (pdfFile) {
      setFile(pdfFile);
      setError('');
      setResult(null);
    } else {
      setError(language === 'nl' ? 'Alleen PDF bestanden worden ondersteund' : 'Only PDF files are supported');
    }
  }, [language]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/pdf': ['.pdf']
    },
    maxFiles: 1,
    maxSize: 10 * 1024 * 1024, // 10MB
    disabled: !isPro()
  });

  const handleConvert = async () => {
    if (!file) return;
    
    setLoading(true);
    setError('');
    setResult(null);

    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post(
        `${API_URL}/api/convert/pdf-to-mt940`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
            'Authorization': `Bearer ${token}`
          }
        }
      );
      
      setResult(response.data);
    } catch (err) {
      setError(
        err.response?.data?.detail ||
        (language === 'nl' ? 'Er ging iets mis bij het converteren' : 'Something went wrong during conversion')
      );
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async () => {
    if (!result?.mt940_content) return;
    
    setDownloading(true);
    try {
      // Create blob and download
      const blob = new Blob([result.mt940_content], { type: 'application/octet-stream' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = result.filename || 'bankafschrift.sta';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } finally {
      setDownloading(false);
    }
  };

  const resetForm = () => {
    setFile(null);
    setResult(null);
    setError('');
  };

  const bankLogos = {
    ING: '🟠',
    RABOBANK: '🔵',
    ABN_AMRO: '🟢',
    GENERIC: '🏦'
  };

  // If not Pro user, show upgrade message
  if (!isPro()) {
    return (
      <div className="min-h-screen py-12 px-4">
        <div className="max-w-2xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-700 p-8 text-center"
          >
            <div className="w-16 h-16 rounded-full bg-accent/10 flex items-center justify-center mx-auto mb-6">
              <Lock className="w-8 h-8 text-accent" />
            </div>
            <h1 className="font-heading font-bold text-2xl text-primary dark:text-white mb-4">
              {language === 'nl' ? 'Pro Feature' : 'Pro Feature'}
            </h1>
            <p className="text-slate-500 dark:text-slate-400 mb-6">
              {language === 'nl'
                ? 'PDF naar MT940 conversie is alleen beschikbaar voor Pro en Team gebruikers.'
                : 'PDF to MT940 conversion is only available for Pro and Team users.'}
            </p>
            <Link
              to="/pricing"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-accent text-white font-medium hover:bg-accent-hover transition-colors"
            >
              <CreditCard className="w-5 h-5" />
              {language === 'nl' ? 'Bekijk Pro plannen' : 'View Pro plans'}
            </Link>
          </motion.div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-12 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-amber-100 dark:bg-amber-900/30 rounded-full text-amber-700 dark:text-amber-300 text-sm font-medium mb-4">
            <FileSpreadsheet className="w-4 h-4" />
            Pro Feature
          </div>
          <h1 className="font-heading font-bold text-3xl sm:text-4xl text-primary dark:text-white mb-4">
            {language === 'nl' ? 'PDF naar MT940 Converter' : 'PDF to MT940 Converter'}
          </h1>
          <p className="text-lg text-slate-500 dark:text-slate-400 max-w-2xl mx-auto">
            {language === 'nl'
              ? 'Converteer je bankafschriften (PDF) naar MT940 formaat voor import in je boekhoudpakket'
              : 'Convert your bank statements (PDF) to MT940 format for import into your accounting software'}
          </p>
        </motion.div>

        {/* Upload Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-700 p-8 mb-8"
        >
          {!result ? (
            <>
              {/* Dropzone */}
              <div
                {...getRootProps()}
                className={`border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-colors ${
                  isDragActive
                    ? 'border-accent bg-accent/5'
                    : file
                    ? 'border-success bg-success/5'
                    : 'border-slate-300 dark:border-slate-600 hover:border-accent'
                }`}
              >
                <input {...getInputProps()} data-testid="mt940-file-input" />
                
                {file ? (
                  <div className="flex items-center justify-center gap-4">
                    <div className="w-12 h-12 rounded-xl bg-success/10 flex items-center justify-center">
                      <FileText className="w-6 h-6 text-success" />
                    </div>
                    <div className="text-left">
                      <p className="font-medium text-slate-900 dark:text-white">{file.name}</p>
                      <p className="text-sm text-slate-500">
                        {(file.size / 1024 / 1024).toFixed(2)} MB
                      </p>
                    </div>
                    <button
                      onClick={(e) => { e.stopPropagation(); resetForm(); }}
                      className="ml-4 text-slate-400 hover:text-error"
                    >
                      ✕
                    </button>
                  </div>
                ) : (
                  <>
                    <Upload className="w-12 h-12 text-slate-400 mx-auto mb-4" />
                    <p className="text-lg font-medium text-slate-700 dark:text-slate-300 mb-2">
                      {isDragActive
                        ? (language === 'nl' ? 'Laat hier los...' : 'Drop here...')
                        : (language === 'nl' ? 'Sleep je PDF hier naartoe' : 'Drag your PDF here')}
                    </p>
                    <p className="text-slate-500 dark:text-slate-400">
                      {language === 'nl' ? 'of klik om te selecteren' : 'or click to select'}
                    </p>
                  </>
                )}
              </div>

              {/* Convert Button */}
              <button
                onClick={handleConvert}
                disabled={!file || loading}
                className="mt-6 w-full py-4 px-6 rounded-xl bg-accent text-white font-semibold text-lg hover:bg-accent-hover transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-3"
                data-testid="convert-button"
              >
                {loading ? (
                  <Loader2 className="w-6 h-6 animate-spin" />
                ) : (
                  <>
                    <ArrowRight className="w-5 h-5" />
                    {language === 'nl' ? 'Converteer naar MT940' : 'Convert to MT940'}
                  </>
                )}
              </button>
            </>
          ) : (
            /* Results */
            <div className="space-y-6">
              {/* Success Header */}
              <div className="flex items-center gap-4 pb-6 border-b border-slate-200 dark:border-slate-700">
                <div className="w-14 h-14 rounded-xl bg-success/10 flex items-center justify-center">
                  <CheckCircle className="w-7 h-7 text-success" />
                </div>
                <div>
                  <h3 className="font-semibold text-lg text-slate-900 dark:text-white">
                    {language === 'nl' ? 'Conversie succesvol!' : 'Conversion successful!'}
                  </h3>
                  <p className="text-slate-500 dark:text-slate-400">
                    {result.filename}
                  </p>
                </div>
              </div>

              {/* Stats Grid */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                <div className="p-4 bg-slate-50 dark:bg-slate-800 rounded-xl">
                  <p className="text-sm text-slate-500 dark:text-slate-400 mb-1">Bank</p>
                  <p className="font-semibold text-slate-900 dark:text-white flex items-center gap-2">
                    <span>{bankLogos[result.bank_detected] || '🏦'}</span>
                    {result.bank_detected}
                  </p>
                </div>
                <div className="p-4 bg-slate-50 dark:bg-slate-800 rounded-xl">
                  <p className="text-sm text-slate-500 dark:text-slate-400 mb-1">
                    {language === 'nl' ? 'Transacties' : 'Transactions'}
                  </p>
                  <p className="font-semibold text-slate-900 dark:text-white">
                    {result.transaction_count}
                  </p>
                </div>
                <div className="p-4 bg-slate-50 dark:bg-slate-800 rounded-xl">
                  <p className="text-sm text-slate-500 dark:text-slate-400 mb-1">
                    {language === 'nl' ? 'Beginsaldo' : 'Opening'}
                  </p>
                  <p className="font-semibold text-slate-900 dark:text-white">
                    €{result.opening_balance?.toFixed(2) || '0.00'}
                  </p>
                </div>
                <div className="p-4 bg-slate-50 dark:bg-slate-800 rounded-xl">
                  <p className="text-sm text-slate-500 dark:text-slate-400 mb-1">
                    {language === 'nl' ? 'Eindsaldo' : 'Closing'}
                  </p>
                  <p className="font-semibold text-slate-900 dark:text-white">
                    €{result.closing_balance?.toFixed(2) || '0.00'}
                  </p>
                </div>
              </div>

              {/* Account Info */}
              {result.account_number && (
                <div className="p-4 bg-slate-50 dark:bg-slate-800 rounded-xl">
                  <p className="text-sm text-slate-500 dark:text-slate-400 mb-1">
                    {language === 'nl' ? 'Rekeningnummer' : 'Account number'}
                  </p>
                  <p className="font-mono font-semibold text-slate-900 dark:text-white">
                    {result.account_number}
                  </p>
                </div>
              )}

              {/* Transaction Preview */}
              {result.transactions && result.transactions.length > 0 && (
                <div>
                  <h4 className="font-medium text-slate-900 dark:text-white mb-3">
                    {language === 'nl' ? 'Transactie preview' : 'Transaction preview'}
                  </h4>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-slate-200 dark:border-slate-700">
                          <th className="text-left py-2 px-3 text-slate-500">Datum</th>
                          <th className="text-right py-2 px-3 text-slate-500">Bedrag</th>
                          <th className="text-left py-2 px-3 text-slate-500">Omschrijving</th>
                        </tr>
                      </thead>
                      <tbody>
                        {result.transactions.slice(0, 5).map((tx, idx) => (
                          <tr key={idx} className="border-b border-slate-100 dark:border-slate-800">
                            <td className="py-2 px-3 text-slate-700 dark:text-slate-300">
                              {tx.date || '-'}
                            </td>
                            <td className={`py-2 px-3 text-right font-medium ${
                              tx.amount >= 0 ? 'text-success' : 'text-error'
                            }`}>
                              {tx.amount >= 0 ? '+' : ''}€{tx.amount?.toFixed(2)}
                            </td>
                            <td className="py-2 px-3 text-slate-600 dark:text-slate-400 max-w-xs truncate">
                              {tx.description || tx.counter_name || '-'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {result.transactions.length > 5 && (
                      <p className="text-sm text-slate-500 mt-2 text-center">
                        {language === 'nl' 
                          ? `En ${result.transactions.length - 5} meer transacties...`
                          : `And ${result.transactions.length - 5} more transactions...`}
                      </p>
                    )}
                  </div>
                </div>
              )}

              {/* Download Buttons */}
              <div className="flex flex-col sm:flex-row gap-4 pt-4">
                <button
                  onClick={handleDownload}
                  disabled={downloading}
                  className="flex-1 py-3 px-6 rounded-xl bg-accent text-white font-medium hover:bg-accent-hover transition-colors flex items-center justify-center gap-2"
                  data-testid="download-mt940-btn"
                >
                  {downloading ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <Download className="w-5 h-5" />
                  )}
                  {language === 'nl' ? 'Download MT940' : 'Download MT940'}
                </button>
                <button
                  onClick={resetForm}
                  className="flex-1 py-3 px-6 rounded-xl bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 font-medium hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors"
                >
                  {language === 'nl' ? 'Nieuwe conversie' : 'New conversion'}
                </button>
              </div>
            </div>
          )}

          {/* Error */}
          <AnimatePresence>
            {error && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                className="mt-6 p-4 bg-error/10 border border-error/20 rounded-xl flex items-start gap-3"
              >
                <AlertCircle className="w-5 h-5 text-error flex-shrink-0 mt-0.5" />
                <p className="text-error">{error}</p>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>

        {/* Supported Banks */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-700 p-6"
        >
          <div className="flex items-center gap-2 mb-4">
            <Building2 className="w-5 h-5 text-accent" />
            <h3 className="font-semibold text-slate-900 dark:text-white">
              {language === 'nl' ? 'Ondersteunde banken' : 'Supported banks'}
            </h3>
          </div>
          <div className="grid grid-cols-3 gap-4">
            <div className="flex items-center gap-3 p-3 bg-slate-50 dark:bg-slate-800 rounded-lg">
              <span className="text-2xl">🟠</span>
              <span className="font-medium text-slate-700 dark:text-slate-300">ING</span>
            </div>
            <div className="flex items-center gap-3 p-3 bg-slate-50 dark:bg-slate-800 rounded-lg">
              <span className="text-2xl">🔵</span>
              <span className="font-medium text-slate-700 dark:text-slate-300">Rabobank</span>
            </div>
            <div className="flex items-center gap-3 p-3 bg-slate-50 dark:bg-slate-800 rounded-lg">
              <span className="text-2xl">🟢</span>
              <span className="font-medium text-slate-700 dark:text-slate-300">ABN AMRO</span>
            </div>
          </div>
        </motion.div>

        {/* Info Box */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3 }}
          className="mt-6 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-xl"
        >
          <div className="flex items-start gap-3">
            <Info className="w-5 h-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-blue-800 dark:text-blue-200">
              <p className="font-medium mb-1">
                {language === 'nl' ? 'Wat is MT940?' : 'What is MT940?'}
              </p>
              <p>
                {language === 'nl'
                  ? 'MT940 is een internationaal standaard formaat voor bankafschriften. Het wordt ondersteund door vrijwel alle boekhoudpakketten zoals Exact, Twinfield, AFAS, Moneybird en meer.'
                  : 'MT940 is an international standard format for bank statements. It is supported by virtually all accounting software such as Exact, Twinfield, AFAS, Moneybird and more.'}
              </p>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default MT940Page;
